import React, { useMemo } from 'react';
import { Box, Button, Card, CardContent, IconButton, TextField, Typography } from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { ClientConfig } from '../../services/module4Service';
import TabPanel from './TabPanel';

interface TabPanelTopicsProps {
  config: ClientConfig;
  activeTab: number;
  handleMappingChange: (mappingType: 'topic_mapping' | 'intent_mapping' | 'funnel_mapping', key: string, value: string) => void;
  handleAddMapping: (mappingType: 'topic_mapping' | 'intent_mapping' | 'funnel_mapping') => void;
  handleRemoveMapping: (mappingType: 'topic_mapping' | 'intent_mapping' | 'funnel_mapping', key: string) => void;
  // Add new handlers for subtopics
  handleSubtopicChange?: (topicId: string, subtopicId: string, value: string) => void;
  handleAddSubtopic?: (topicId: string) => void;
  handleRemoveSubtopic?: (topicId: string, subtopicId: string) => void;
}

const TabPanelTopics: React.FC<TabPanelTopicsProps> = ({
  config,
  activeTab,
  handleMappingChange,
  handleAddMapping,
  handleRemoveMapping,
  handleSubtopicChange,
  handleAddSubtopic,
  handleRemoveSubtopic
}) => {
  // Helper function to get the topic name from either string or object format
  const getTopicName = (topicValue: any): string => {
    if (typeof topicValue === 'string') {
      return topicValue;
    } else if (topicValue && typeof topicValue === 'object' && topicValue.name) {
      return topicValue.name;
    }
    return '';
  };

  // Helper function to get the subtopic name from either string or object format
  const getSubtopicName = (subtopicValue: any): string => {
    if (typeof subtopicValue === 'string') {
      return subtopicValue;
    } else if (subtopicValue && typeof subtopicValue === 'object' && subtopicValue.name) {
      return subtopicValue.name;
    }
    return '';
  };

  // Deduplicate topics by name, keeping the first occurrence of each unique name
  const deduplicatedTopics = useMemo(() => {
    if (!config.topic_mapping) return [];
    
    const seenNames = new Set<string>();
    const uniqueTopics: Array<[string, any]> = [];
    const duplicatesCount = { total: 0, filtered: 0 };
    
    Object.entries(config.topic_mapping as Record<string, any>).forEach(([topicId, topicValue]) => {
      const topicName = getTopicName(topicValue);
      duplicatesCount.total++;
      
      if (topicName && !seenNames.has(topicName)) {
        seenNames.add(topicName);
        uniqueTopics.push([topicId, topicValue]);
      } else if (topicName && seenNames.has(topicName)) {
        duplicatesCount.filtered++;
        console.warn(`Duplicate topic detected and filtered: "${topicName}" (ID: ${topicId})`);
      }
    });
    
    if (duplicatesCount.filtered > 0) {
      console.log(`Topics deduplication: ${duplicatesCount.total} total topics, ${duplicatesCount.filtered} duplicates filtered, ${uniqueTopics.length} unique topics remaining`);
    }
    
    return uniqueTopics;
  }, [config.topic_mapping]);

  // Update the no topics message to be more helpful
  const noTopicsMessage = config.topic_mapping && Object.keys(config.topic_mapping).length > 0 
    ? "All topics were filtered due to duplicates. Please check the topic configuration."
    : "No topic mappings defined.";

  return (
    <TabPanel value={activeTab} index={1}>
      <Typography variant="h6" gutterBottom>
        Topic and Subtopic Definitions
      </Typography>
      <Card>
        <CardContent>
          {/* Check if deduplicatedTopics exists and has entries */}
          {deduplicatedTopics.length > 0 ? (
            deduplicatedTopics.map(([parentTopicId, parentTopicValue]) => {
              const parentTopicName = getTopicName(parentTopicValue);
              
              return (
                <Box key={parentTopicId} mb={2} pl={0} /* Parent topic styling */ >
                  {/* Display Parent Topic */}
                  <Box display="flex" alignItems="center" mb={0.5}>
                    <TextField
                      fullWidth
                      label="Topic ID"
                      value={parentTopicId}
                      disabled
                      sx={{ mr: 1, maxWidth: '150px' }} // Adjust width as needed
                    />
                    <TextField
                      fullWidth
                      label="Topic Name"
                      value={parentTopicName}
                      onChange={(e) => handleMappingChange('topic_mapping', parentTopicId, e.target.value)}
                      sx={{ mr: 1, flexGrow: 1 }}
                    />
                    <IconButton
                      color="error"
                      onClick={() => {
                        handleRemoveMapping('topic_mapping', parentTopicId);
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>

                  
                  {config.subtopic_mappings && 
                   config.subtopic_mappings[parentTopicId] && 
                   config.subtopic_mappings[parentTopicId].subtopics &&
                   Object.keys(config.subtopic_mappings[parentTopicId].subtopics).length > 0 && (
                    <Box ml={4} /* Indent subtopics */ >
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, mt: 0.5 }}>
                        Subtopics:
                      </Typography>
                      {Object.entries(config.subtopic_mappings[parentTopicId].subtopics as Record<string, any>).map(([subtopicId, subtopicValue]) => {
                        const subtopicName = getSubtopicName(subtopicValue);
                        
                        return (
                          <Box key={subtopicId} display="flex" alignItems="center" mb={0.5} pl={2}>
                            <TextField
                              fullWidth
                              label="Subtopic ID"
                              value={subtopicId}
                              disabled
                              sx={{ mr: 1, maxWidth: '150px' }} // Adjust width
                            />
                            <TextField
                              fullWidth
                              label="Subtopic Name"
                              value={subtopicName}
                              onChange={(e) => handleSubtopicChange?.(parentTopicId, subtopicId, e.target.value)}
                              sx={{ mr: 1, flexGrow: 1 }}
                            />
                            <IconButton
                              color="error"
                              onClick={() => handleRemoveSubtopic?.(parentTopicId, subtopicId)}
                              size="small"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        );
                      })}
                      {/* Add Subtopic Button */}
                      <Box pl={2} mt={1}>
                        <Button
                          variant="text"
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() => handleAddSubtopic?.(parentTopicId)}
                          sx={{ textTransform: 'none' }}
                        >
                          Add Subtopic
                        </Button>
                      </Box>
                    </Box>
                  )}
                  
                  {/* Show Add Subtopic button even if no subtopics exist yet */}
                  {(!config.subtopic_mappings?.[parentTopicId] || 
                    !config.subtopic_mappings[parentTopicId].subtopics ||
                    Object.keys(config.subtopic_mappings[parentTopicId].subtopics).length === 0) && (
                    <Box ml={4} mt={1}>
                      <Button
                        variant="text"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => handleAddSubtopic?.(parentTopicId)}
                        sx={{ textTransform: 'none', color: 'text.secondary' }}
                      >
                        Add Subtopic
                      </Button>
                    </Box>
                  )}
                </Box>
              );
            })
          ) : (
            <Typography>{noTopicsMessage}</Typography>
          )}
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => handleAddMapping('topic_mapping')}
            sx={{ mt: 2 }}
          >
            Add Topic Mapping
          </Button>
        </CardContent>
      </Card>
    </TabPanel>
  );
};

export default TabPanelTopics;
